'use client';

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import WarningBanner from '@/components/common/WarningBanner';

interface NavItem {
  label: string;
  path: string;
  icon: string;
  shortcut: string;
}

const NAV_ITEMS: NavItem[] = [
  {
    label: 'Dashboard',
    path: '/access-control-listings-management-dashboard',
    icon: 'TableCellsIcon',
    shortcut: 'Alt+1',
  },
  {
    label: 'New Listing',
    path: '/comprehensive-listing-editor-panel',
    icon: 'PencilSquareIcon',
    shortcut: 'Alt+2',
  },
  {
    label: 'Users',
    path: '/profile-search-user-management-interface',
    icon: 'UsersIcon',
    shortcut: 'Alt+3',
  },
];

interface HeaderProps {
  isOpen?: boolean;
}

const Header = ({ isOpen = false }: HeaderProps) => {
  const pathname = usePathname();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(isOpen);

  const handleKeyboardShortcuts = useCallback(
    (e: KeyboardEvent) => {
      if (e.altKey) {
        const index = parseInt(e.key) - 1;
        if (index >= 0 && index < NAV_ITEMS.length) {
          e.preventDefault();
          window.location.href = NAV_ITEMS[index].path;
        }
      }
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    },
    [mobileMenuOpen]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyboardShortcuts);
    return () => document.removeEventListener('keydown', handleKeyboardShortcuts);
  }, [handleKeyboardShortcuts]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  const isActive = (path: string) => pathname === path;

  return (
    <>
      <div className="fixed top-0 left-0 right-0" style={{ zIndex: 400 }}>
        <WarningBanner dismissible={true} />

        {/* Navigation Bar */}
        <header
          className="w-full bg-card border-b border-border shadow-sm"
          style={{ zIndex: 100 }}
          role="banner"
        >
          <div className="flex items-center h-14 px-4 md:px-6 gap-6">
            {/* Logo */}
            <Link
              href="/access-control-listings-management-dashboard"
              className="flex items-center gap-2 shrink-0 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded"
              aria-label="Go to Dashboard"
            >
              <svg
                width="28"
                height="28"
                viewBox="0 0 28 28"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <rect width="28" height="28" rx="6" fill="var(--color-primary)" />
                <rect x="6" y="7" width="7" height="7" rx="1.5" fill="white" />
                <rect x="15" y="7" width="7" height="7" rx="1.5" fill="white" fillOpacity="0.6" />
                <rect x="6" y="16" width="7" height="5" rx="1.5" fill="white" fillOpacity="0.6" />
                <rect x="15" y="16" width="7" height="5" rx="1.5" fill="white" />
              </svg>
              <span className="font-heading font-600 text-sm text-foreground tracking-tight hidden sm:block">
                AdminDB
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav
              className="hidden md:flex items-center gap-1 flex-1"
              role="navigation"
              aria-label="Primary navigation"
            >
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.path}
                  href={item.path}
                  title={`${item.label} (${item.shortcut})`}
                  aria-current={isActive(item.path) ? 'page' : undefined}
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-all duration-200
                    focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1
                    ${
                      isActive(item.path)
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }
                  `}
                >
                  <Icon
                    name={item.icon as any}
                    size={16}
                    variant={isActive(item.path) ? 'solid' : 'outline'}
                  />
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* Right side info */}
            <div className="hidden md:flex items-center gap-3 ml-auto">
              <span className="font-caption text-xs text-muted-foreground">
                Internal Admin v2.1
              </span>
              <div className="w-px h-4 bg-border" />
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-success" aria-hidden="true" />
                <span className="font-caption text-xs text-muted-foreground">Connected</span>
              </div>
            </div>

            {/* Mobile hamburger */}
            <button
              className="md:hidden ml-auto p-2 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu"
              aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            >
              <Icon name={mobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={22} variant="outline" />
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div
              id="mobile-menu"
              className="md:hidden bg-card border-t border-border"
              role="navigation"
              aria-label="Mobile navigation"
            >
              <div className="px-4 py-3 flex flex-col gap-1">
                {NAV_ITEMS.map((item) => (
                  <Link
                    key={item.path}
                    href={item.path}
                    aria-current={isActive(item.path) ? 'page' : undefined}
                    className={`
                      flex items-center gap-3 px-4 py-3 rounded text-sm font-medium transition-all duration-200
                      focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1
                      ${
                        isActive(item.path)
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }
                    `}
                  >
                    <Icon
                      name={item.icon as any}
                      size={18}
                      variant={isActive(item.path) ? 'solid' : 'outline'}
                    />
                    <span>{item.label}</span>
                    <span className="ml-auto font-caption text-xs opacity-60">{item.shortcut}</span>
                  </Link>
                ))}
                <div className="mt-2 pt-2 border-t border-border flex items-center justify-between px-1">
                  <span className="font-caption text-xs text-muted-foreground">Internal Admin v2.1</span>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-success" aria-hidden="true" />
                    <span className="font-caption text-xs text-muted-foreground">Connected</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </header>
      </div>

      {/* Spacer to offset fixed header — warning banner (~36px) + nav bar (56px) */}
      <div className="h-[92px]" aria-hidden="true" />
    </>
  );
};

export default Header;